import { useCallback, useEffect, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import { Loader2, AlertTriangle, Check, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { resolveBirthTimezone, type ResolvedBirthTimezone } from '@/services'

function parseManualOffset(raw: string): number | null {
    const text = raw.trim()
    if (!text) return null
    if (/^[+-]?\d+(?:\.\d+)?$/.test(text)) {
        const decimal = Number(text)
        return Number.isFinite(decimal) ? decimal : null
    }
    const match = /^([+-]?)(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/.exec(text)
    if (!match) return null
    const sign = match[1] === '-' ? -1 : 1
    const hours = Number(match[2])
    const minutes = Number(match[3])
    const seconds = Number(match[4] || 0)
    return sign * (hours + minutes / 60 + seconds / 3600)
}

/**
 * The UTC offset in force at a BIRTH INSTANT, resolved server-side.
 *
 * WHY A DEDICATED FIELD: an offset is not a property of a place. New York was
 * UTC-5 in December 1966 and is UTC-4 today; India ran UTC+6:30 during the war.
 * The previous UI asked a "what time is it there now" API and rounded the answer
 * onto a quarter-hour dropdown, which is wrong twice over — wrong question, and
 * lossy for zones like pre-1906 Calcutta (UTC+05:21:10).
 *
 * Used by the birth-chart form and by each partner in the compatibility form.
 * Every instance owns its own state, so one partner being unresolvable never
 * affects the other.
 *
 * ADVISORY ONLY. The backend re-resolves on every calculation route, so this
 * cannot pin a wrong offset onto a chart — it exists to tell the user, before
 * they submit, what will actually be used, and to ask when the answer is genuinely
 * ambiguous (the repeated hour when clocks go back).
 */

export interface BirthTimezoneState {
    /** Offset in hours to submit. Null when nothing usable has been determined. */
    offsetHours: number | null
    /** IANA zone actually used, when known. Sent to the backend as a hint. */
    timezoneName: string | null
    status: ResolvedBirthTimezone['status'] | 'idle' | 'resolving' | 'error' | 'manual'
    /** False when the form must not submit: unanswered ambiguity, impossible time. */
    usable: boolean
    source: 'auto' | 'manual' | null
    fold: number | null
    resolverVersion: number | null
}

interface Props {
    birthDate: string
    birthTime: string
    latitude: string
    longitude: string
    /** IANA zone from the place picker, when the provider supplied one. */
    timezoneName?: string
    /** Current offset as held by the parent form, used to seed manual mode. */
    value: string
    /** Persisted provenance for `value`; manual values must not be auto-resolved on restore. */
    source: '' | 'auto' | 'manual'
    onChange: (next: {
        timezoneOffset: string
        timezoneName?: string
        timezoneSource: '' | 'auto' | 'manual'
        timezoneFold: number | null
        timezoneResolverVersion: number | null
    }) => void
    onStateChange?: (state: BirthTimezoneState) => void
}

export function BirthTimezoneField({
    birthDate,
    birthTime,
    latitude,
    longitude,
    timezoneName,
    value,
    source,
    onChange,
    onStateChange,
}: Props) {
    const { t } = useTranslation('chat')
    const [result, setResult] = useState<ResolvedBirthTimezone | null>(null)
    const [resolving, setResolving] = useState(false)
    const [manual, setManual] = useState(false)
    const [manualText, setManualText] = useState('')
    const [failed, setFailed] = useState(false)
    const [retryNonce, setRetryNonce] = useState(0)

    // Guards a late response from overwriting a newer one. Incremented on every
    // input change, so only the most recent request may commit.
    const seqRef = useRef(0)
    const abortRef = useRef<AbortController | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const hasCoords = Boolean(latitude && longitude)
    const canResolve = Boolean(birthDate && birthTime) && (hasCoords || Boolean(timezoneName))
    const hasManualSource = source === 'manual'

    // Callbacks are held in refs so a parent passing inline arrows — which every
    // render recreates — cannot retrigger resolution or loop the effect.
    const onStateChangeRef = useRef(onStateChange)
    useEffect(() => {
        onStateChangeRef.current = onStateChange
    }, [onStateChange])

    const onChangeRef = useRef(onChange)
    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange])

    const report = useCallback((state: BirthTimezoneState) => {
        onStateChangeRef.current?.(state)
    }, [])

    useEffect(() => {
        if (source !== 'manual') return

        ++seqRef.current
        abortRef.current?.abort()
        if (debounceRef.current) clearTimeout(debounceRef.current)

        const parsed = parseManualOffset(value)
        const usable = parsed !== null && parsed >= -14 && parsed <= 14
        setManual(true)
        setManualText(value)
        setResult(null)
        setFailed(false)
        setResolving(false)
        report({
            offsetHours: usable ? parsed : null,
            timezoneName: null,
            status: 'manual',
            usable,
            source: usable ? 'manual' : null,
            fold: null,
            resolverVersion: null,
        })
    }, [source, value, report])

    useEffect(() => {
        if (manual || hasManualSource) return

        if (!canResolve) {
            ++seqRef.current
            abortRef.current?.abort()
            setResult(null)
            setFailed(false)
            setResolving(false)
            onChangeRef.current({
                timezoneOffset: '',
                timezoneName: timezoneName || '',
                timezoneSource: '',
                timezoneFold: null,
                timezoneResolverVersion: null,
            })
            report({
                offsetHours: null,
                timezoneName: null,
                status: 'idle',
                usable: false,
                source: null,
                fold: null,
                resolverVersion: null,
            })
            return
        }

        const seq = ++seqRef.current
        abortRef.current?.abort()
        if (debounceRef.current) clearTimeout(debounceRef.current)

        setResolving(true)
        setFailed(false)
        // Date/time/place changed. Clear the previous city's usable offset
        // before starting the request so a fast submit cannot race this lookup.
        onChangeRef.current({
            timezoneOffset: '',
            timezoneName: timezoneName || '',
            timezoneSource: '',
            timezoneFold: null,
            timezoneResolverVersion: null,
        })
        report({
            offsetHours: null,
            timezoneName: null,
            status: 'resolving',
            usable: false,
            source: null,
            fold: null,
            resolverVersion: null,
        })

        debounceRef.current = setTimeout(async () => {
            const controller = new AbortController()
            abortRef.current = controller
            try {
                const res = await resolveBirthTimezone(
                    {
                        localDate: birthDate,
                        localTime: birthTime,
                        timezoneName: timezoneName || null,
                        latitude: latitude ? parseFloat(latitude) : null,
                        longitude: longitude ? parseFloat(longitude) : null,
                    },
                    { signal: controller.signal },
                )
                if (seq !== seqRef.current) return
                setResult(res)
                setResolving(false)

                if (res.status === 'resolved' && typeof res.offset_hours === 'number') {
                    onChangeRef.current({
                        timezoneOffset: String(res.offset_hours),
                        timezoneName: res.timezone_name || undefined,
                        timezoneSource: 'auto',
                        timezoneFold: res.fold ?? null,
                        timezoneResolverVersion: res.resolver_version ?? null,
                    })
                    report({
                        offsetHours: res.offset_hours,
                        timezoneName: res.timezone_name ?? null,
                        status: 'resolved',
                        usable: true,
                        source: 'auto',
                        fold: res.fold ?? null,
                        resolverVersion: res.resolver_version ?? null,
                    })
                } else {
                    report({
                        offsetHours: null,
                        timezoneName: res.timezone_name ?? null,
                        status: res.status,
                        usable: false,
                        source: null,
                        fold: null,
                        resolverVersion: res.resolver_version ?? null,
                    })
                }
            } catch (e: any) {
                if (e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return
                if (seq !== seqRef.current) return
                setResolving(false)
                setFailed(true)
                setResult(null)
                report({
                    offsetHours: null,
                    timezoneName: null,
                    status: 'error',
                    usable: false,
                    source: null,
                    fold: null,
                    resolverVersion: null,
                })
            }
        }, 350)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [birthDate, birthTime, latitude, longitude, timezoneName, manual, hasManualSource, canResolve, report, retryNonce])

    useEffect(() => () => abortRef.current?.abort(), [])

    const applyManual = (raw: string) => {
        const parsed = parseManualOffset(raw)
        const usable = parsed !== null && parsed >= -14 && parsed <= 14
        onChangeRef.current({
            timezoneOffset: usable ? String(parsed) : '',
            timezoneName: undefined,
            timezoneSource: usable ? 'manual' : '',
            timezoneFold: null,
            timezoneResolverVersion: null,
        })
        report({
            offsetHours: usable ? parsed : null,
            timezoneName: null,
            status: 'manual',
            usable,
            source: usable ? 'manual' : null,
            fold: null,
            resolverVersion: null,
        })
    }

    const chooseFold = (offsetHours: number, label: string, fold: number) => {
        onChangeRef.current({
            timezoneOffset: String(offsetHours),
            timezoneName: result?.timezone_name || undefined,
            timezoneSource: 'auto',
            timezoneFold: fold,
            timezoneResolverVersion: result?.resolver_version ?? null,
        })
        setResult({
            ...(result as ResolvedBirthTimezone),
            status: 'resolved',
            offset_hours: offsetHours,
            offset_label: label,
            fold,
        })
        report({
            offsetHours,
            timezoneName: result?.timezone_name ?? null,
            status: 'resolved',
            usable: true,
            source: 'auto',
            fold,
            resolverVersion: result?.resolver_version ?? null,
        })
    }

    const label = t('chat.location_input.timezone_utc')

    // ---- Manual mode -------------------------------------------------------
    if (manual) {
        return (
            <>
                <Label className="flex items-center gap-2 h-5">
                    {label}
                    <button
                        type="button"
                        className="text-xs font-normal text-blue-600 hover:underline dark:text-blue-400"
                        onClick={() => {
                            onChangeRef.current({
                                timezoneOffset: '',
                                timezoneName: timezoneName || '',
                                timezoneSource: '',
                                timezoneFold: null,
                                timezoneResolverVersion: null,
                            })
                            setManual(false)
                            setFailed(false)
                            setResult(null)
                        }}
                    >
                        {t('chat.birth_timezone.use_auto', { defaultValue: 'Auto' })}
                    </button>
                </Label>
                <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <input
                        type="text"
                        inputMode="decimal"
                        className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                        value={manualText}
                        placeholder="+05:30 or 5.5"
                        onChange={(e) => {
                            setManualText(e.target.value)
                            applyManual(e.target.value)
                        }}
                    />
                </div>
            </>
        )
    }

    // ---- Auto mode ---------------------------------------------------------
    return (
        <>
            <Label className="flex items-center gap-2 h-5">
                {label}
                {resolving && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-gray-500 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={() => {
                        setManual(true)
                        setManualText(value)
                        applyManual(value)
                    }}
                >
                    <Pencil className="h-3 w-3" />
                    {t('chat.birth_timezone.set_manually', { defaultValue: 'Set manually' })}
                </button>
            </Label>

            <div className="flex min-h-10 items-center rounded-md border border-input bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
                {!canResolve && (
                    <span className="text-sm text-gray-400">
                        {t('chat.birth_timezone.awaiting_inputs', {
                            defaultValue: 'Enter birth date, time and place',
                        })}
                    </span>
                )}

                {canResolve && resolving && (
                    <span className="text-sm text-gray-500">
                        {t('chat.birth_timezone.resolving', { defaultValue: 'Checking…' })}
                    </span>
                )}

                {canResolve && !resolving && failed && (
                    <span className="flex w-full items-center justify-between gap-3 text-sm text-gray-500">
                        <span>
                            {t('chat.birth_timezone.lookup_failed', {
                                defaultValue: 'Could not check automatically',
                            })}
                        </span>
                        <button
                            type="button"
                            className="shrink-0 font-medium text-blue-600 hover:underline dark:text-blue-400"
                            onClick={() => setRetryNonce((value) => value + 1)}
                        >
                            {t('chat.location_input.retry', { defaultValue: 'Retry' })}
                        </button>
                    </span>
                )}

                {!resolving && result?.status === 'resolved' && (
                    <span className="flex items-center gap-2 text-sm">
                        <Check className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
                        <span className="font-medium tabular-nums">{result.offset_label}</span>
                    </span>
                )}

                {!resolving && result && result.status !== 'resolved' && (
                    <span className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            {result.status === 'nonexistent' &&
                                t('chat.birth_timezone.nonexistent', {
                                    defaultValue:
                                        'That clock time did not exist here — the clocks jumped forward. Please check the birth time.',
                                })}
                            {result.status === 'ambiguous' &&
                                t('chat.birth_timezone.ambiguous', {
                                    defaultValue:
                                        'The clocks went back, so this time happened twice. Which one?',
                                })}
                            {result.status === 'manual_required' &&
                                t('chat.birth_timezone.manual_required', {
                                    defaultValue:
                                        'This date is too early for reliable timezone records. Please set the offset manually.',
                                })}
                            {result.status === 'invalid' &&
                                t('chat.birth_timezone.invalid', {
                                    defaultValue: 'Please check the birth date and time.',
                                })}
                        </span>
                    </span>
                )}
            </div>

            {/* The user is the only one who can answer a repeated-hour ambiguity. */}
            {!resolving && result?.status === 'ambiguous' && Array.isArray(result.choices) && (
                <div className="flex flex-wrap gap-2 pt-1">
                    {result.choices.map((c) => (
                        <button
                            key={c.fold}
                            type="button"
                            onClick={() => chooseFold(c.offset_hours, c.label, c.fold)}
                            className="rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium tabular-nums hover:bg-accent"
                        >
                            {c.label}
                            <span className="ml-1.5 font-normal text-gray-500">
                                {c.fold === 0
                                    ? t('chat.birth_timezone.first_occurrence', { defaultValue: 'first' })
                                    : t('chat.birth_timezone.second_occurrence', { defaultValue: 'second' })}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {!resolving && result?.status === 'resolved' && result.timezone_name && (
                <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">{result.timezone_name}</p>
            )}
        </>
    )
}
